import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2, Music, Pause, Play, Radio, Volume2, VolumeX, X } from 'lucide-react'
import { getAudioImageUrl, getChannelCoverUrl, resolveMediaUrl, type Channel } from '../../../api'
import { adminMediaPlayerClassName } from '../../../components/ui'
import styles from './AdminStreamPlayer.module.css'
import streamIndicators from '../styles/streamIndicators.module.css'
import { useStreamNowPlaying } from '../useStreamNowPlaying'

type AdminStreamPlayerProps = {
  channel: Channel | null
  playbackCommand: { id: number; action: 'toggle' } | null
  streamUrl: string | null
  onClose: () => void
  onPlaybackStateChange: (state: { loading: boolean; playing: boolean }) => void
}

const pc = adminMediaPlayerClassName

export const AdminStreamPlayer = ({
  channel,
  playbackCommand,
  streamUrl,
  onClose,
  onPlaybackStateChange
}: AdminStreamPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const handledPlaybackCommandIdRef = useRef<number | null>(null)
  const volumeRef = useRef<HTMLDivElement>(null)
  const [playing, setPlaying] = useState(false)
  const [loading, setLoading] = useState(false)
  const [muted, setMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const {
    loading: nowPlayingLoading,
    track: nowPlayingTrack
  } = useStreamNowPlaying(channel?.slug ?? null, Boolean(channel && streamUrl))

  useEffect(() => {
    onPlaybackStateChange({ loading, playing })
  }, [loading, onPlaybackStateChange, playing])

  useEffect(() => {
    const audio = audioRef.current

    if (!audio || !streamUrl) {
      return
    }

    setLoading(true)
    onPlaybackStateChange({ loading: true, playing: false })
    audio.src = streamUrl
    audio.volume = volume
    audio.muted = muted

    void audio.play().catch(() => {
      setPlaying(false)
    }).finally(() => {
      setLoading(false)
    })

    return () => {
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
      setPlaying(false)
      setLoading(false)
      onPlaybackStateChange({ loading: false, playing: false })
    }
  }, [streamUrl])

  useEffect(() => {
    if (!audioRef.current) {
      return
    }

    audioRef.current.volume = volume
    audioRef.current.muted = muted
  }, [muted, volume])

  const togglePlayback = useCallback((): void => {
    const audio = audioRef.current

    if (!audio) {
      return
    }

    if (playing) {
      audio.pause()
      setPlaying(false)
      return
    }

    setLoading(true)
    void audio.play().then(() => {
      setPlaying(true)
    }).catch(() => {
      setPlaying(false)
    }).finally(() => {
      setLoading(false)
    })
  }, [playing])

  useEffect(() => {
    if (!playbackCommand) {
      return
    }

    if (handledPlaybackCommandIdRef.current === playbackCommand.id) {
      return
    }

    handledPlaybackCommandIdRef.current = playbackCommand.id

    if (playbackCommand.action === 'toggle') {
      togglePlayback()
    }
  }, [playbackCommand, togglePlayback])

  const setVolumeTo = useCallback((event: React.MouseEvent<HTMLDivElement>): void => {
    if (!volumeRef.current) {
      return
    }

    const rect = volumeRef.current.getBoundingClientRect()
    const nextVolume = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
    setVolume(nextVolume)
    setMuted(nextVolume === 0)
  }, [])

  const close = useCallback((): void => {
    audioRef.current?.pause()
    setPlaying(false)
    onClose()
  }, [onClose])

  if (!channel || !streamUrl) {
    return null
  }

  const channelCoverUrl = getChannelCoverUrl(channel, 'thumb')
  const trackArtworkUrl = nowPlayingTrack ? getAudioImageUrl(nowPlayingTrack, 'preview') : null
  const artworkUrl = trackArtworkUrl ?? channelCoverUrl
  const title = nowPlayingTrack?.title ?? channel.name
  const secondaryText = nowPlayingTrack
    ? nowPlayingTrack.artist ?? 'Live stream'
    : nowPlayingLoading
      ? 'Waiting for current track...'
      : 'Live stream'

  return (
    <div className={pc('admin-audio-player', styles.player)}>
      <audio
        ref={audioRef}
        onCanPlay={() => setLoading(false)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      <div className={pc('admin-audio-player-track')}>
        <div className={pc('admin-audio-player-thumb', artworkUrl && 'has-image')}>
          {artworkUrl ? (
            <img alt="" src={resolveMediaUrl(artworkUrl)} />
          ) : nowPlayingTrack ? (
            <Music size={18} />
          ) : (
            <Radio size={18} />
          )}
        </div>
        <div className={pc('admin-audio-player-info')}>
          <div className={pc('admin-audio-player-title-row')}>
            <span className={pc('admin-audio-player-title')} title={title}>{title}</span>
            {nowPlayingTrack?.source_quality === 'flac' && <span className={streamIndicators.qualityBadge}>HQ</span>}
          </div>
          <span className={pc('admin-audio-player-artist')} title={secondaryText}>{secondaryText}</span>
        </div>
      </div>

      <div className={pc('admin-audio-player-controls')}>
        <button
          className={pc('admin-audio-player-btn', 'admin-audio-player-play-btn')}
          disabled={loading}
          onClick={togglePlayback}
          type="button"
          aria-label={loading ? 'Loading stream' : playing ? 'Pause stream' : 'Play stream'}
        >
          {loading ? <Loader2 className={pc('admin-audio-player-spinner')} size={16} /> : playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <span className={styles.liveIndicator ?? ''}>
          <span className={[streamIndicators.statusDot, streamIndicators.live].join(' ')} aria-hidden="true" />
          Live
        </span>
        <div className={pc('admin-audio-player-progress', styles.progress)} aria-hidden="true">
          <div className={pc('admin-audio-player-progress-fill', styles.progressFill)} />
        </div>
      </div>

      <div className={pc('admin-audio-player-volume')}>
        <button
          className={pc('admin-audio-player-btn')}
          onClick={() => setMuted((current) => !current)}
          type="button"
          aria-label={muted ? 'Unmute stream' : 'Mute stream'}
        >
          {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
        </button>
        <div
          className={pc('admin-audio-player-volume-bar')}
          onClick={setVolumeTo}
          ref={volumeRef}
          role="slider"
          aria-label="Stream volume"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round((muted ? 0 : volume) * 100)}
        >
          <div className={pc('admin-audio-player-volume-fill')} style={{ width: `${(muted ? 0 : volume) * 100}%` }} />
        </div>
        <button
          className={pc('admin-audio-player-btn', 'admin-audio-player-stop')}
          onClick={close}
          type="button"
          aria-label="Stop and close stream player"
        >
          <X size={15} />
        </button>
      </div>
    </div>
  )
}
